import React, { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from './ui/alert-dialog';
import { Upload, FileText, Download, Trash2, Calendar, User, Plus, Search } from 'lucide-react';
import {
  apiListEmployees, apiUploadPayslip, apiListPayslips,
  apiDownloadPayslip, apiDeletePayslip
} from '../api';
import { toast } from 'sonner';

const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const fmtMonth = (m) => {
  if (!m) return '-';
  const [y, mo] = m.split('-');
  return `${MONTHS[parseInt(mo) - 1] || mo} ${y}`;
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = reader.result;
    // Strip data URL prefix
    const base64 = result.split(',')[1];
    resolve(base64);
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export const PayslipUploadDialog = ({ open, onOpenChange, onUploaded, employees }) => {
  const [employeeId, setEmployeeId] = useState('');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const activeEmployees = useMemo(
    () => employees.filter(e => e.role === 'employee' && e.active),
    [employees]
  );

  const reset = () => {
    setEmployeeId(''); setMonth(new Date().toISOString().slice(0, 7));
    setFile(null); setNotes('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!employeeId) { toast.error('Seleziona il dipendente'); return; }
    if (!file) { toast.error('Carica il PDF della busta paga'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('File troppo grande (max 10MB)'); return; }
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Solo file PDF accettati');
      return;
    }
    setUploading(true);
    try {
      const base64 = await fileToBase64(file);
      await apiUploadPayslip({
        employee_id: employeeId,
        month,
        file_name: file.name,
        file_base64: base64,
        notes
      });
      toast.success('Busta paga caricata');
      reset();
      onOpenChange(false);
      onUploaded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore caricamento');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Carica busta paga</DialogTitle>
          <DialogDescription>Seleziona il dipendente e il mese, poi carica il PDF.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Dipendente <span className="text-red-500">*</span></Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger><SelectValue placeholder="Seleziona dipendente..." /></SelectTrigger>
              <SelectContent>
                {activeEmployees.map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.name} — {e.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="psmonth">Mese di riferimento <span className="text-red-500">*</span></Label>
            <Input id="psmonth" type="month" value={month} onChange={(e) => setMonth(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label>File PDF <span className="text-red-500">*</span></Label>
            <label className="flex items-center justify-center gap-2 h-24 rounded-lg border-2 border-dashed border-slate-300 text-sm text-slate-600 hover:border-blue-400 hover:bg-blue-50/30 cursor-pointer transition-colors">
              <Upload className="w-5 h-5" />
              {file ? <span className="text-blue-700 font-medium">{file.name}</span> : <span>Clicca per selezionare (max 10MB)</span>}
              <input type="file" accept=".pdf,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" />
            </label>
          </div>
          <div className="space-y-2">
            <Label htmlFor="psnotes">Note (opzionale)</Label>
            <Textarea id="psnotes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="resize-none" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" disabled={uploading} className="bg-blue-700 hover:bg-blue-800">
              {uploading ? 'Caricamento...' : 'Carica'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const downloadBlob = async (payslip) => {
  try {
    const full = await apiDownloadPayslip(payslip.id);
    const byteChars = atob(full.file_base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = full.file_name || `busta-paga-${full.month}.pdf`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (err) {
    toast.error('Errore download');
  }
};

export const PayslipManagerHR = () => {
  const [payslips, setPayslips] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [search, setSearch] = useState('');

  const reload = async () => {
    const [ps, emps] = await Promise.all([apiListPayslips(), apiListEmployees()]);
    setPayslips(ps || []);
    setEmployees(emps || []);
  };
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await apiDeletePayslip(toDelete.id);
      toast.success('Busta paga eliminata');
      setToDelete(null);
      reload();
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  const filtered = payslips.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return p.employee_name?.toLowerCase().includes(q) || p.employee_email?.toLowerCase().includes(q) || p.month?.includes(q);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Cerca dipendente o mese..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 pl-8 w-[280px] text-sm" />
        </div>
        <Button onClick={() => setUploadOpen(true)} className="bg-blue-700 hover:bg-blue-800 gap-2">
          <Plus className="w-4 h-4" /> Carica busta paga
        </Button>
      </div>

      <PayslipUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onUploaded={reload} employees={employees} />

      {filtered.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="text-sm font-medium text-slate-700">Nessuna busta paga caricata</div>
          <p className="text-xs text-slate-500 mt-1">Clicca "Carica busta paga" per iniziare</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(ps => (
            <div key={ps.id} className="p-4 rounded-xl border border-slate-200 bg-white">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-slate-900 capitalize">{fmtMonth(ps.month)}</div>
                  <div className="text-xs text-slate-600 flex items-center gap-1 mt-0.5">
                    <User className="w-3 h-3" />{ps.employee_name}
                  </div>
                  <div className="text-[11px] text-slate-400 truncate">{ps.employee_email}</div>
                </div>
              </div>
              {ps.notes && <div className="text-xs bg-slate-50 rounded p-2 mb-2 text-slate-700">{ps.notes}</div>}
              <div className="flex items-center justify-between gap-2">
                <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs flex-1" onClick={() => downloadBlob(ps)}>
                  <Download className="w-3 h-3" /> Scarica PDF
                </Button>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50" onClick={() => setToDelete(ps)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare busta paga?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare la busta paga di {toDelete?.employee_name} per {fmtMonth(toDelete?.month)}. Azione non reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Employee view - their own payslips
export const PayslipListEmployee = () => {
  const [payslips, setPayslips] = useState([]);

  useEffect(() => {
    apiListPayslips().then(setPayslips).catch(() => setPayslips([]));
  }, []);

  return (
    <div className="space-y-3">
      {payslips.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="text-sm font-medium text-slate-700">Nessuna busta paga disponibile</div>
          <p className="text-xs text-slate-500 mt-1">L'ufficio HR non ha ancora caricato le tue buste paga</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {payslips.map(ps => (
            <div key={ps.id} className="p-4 rounded-xl border border-slate-200 bg-white hover:border-blue-300 transition-colors">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-11 h-11 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 capitalize">{fmtMonth(ps.month)}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                    <Calendar className="w-3 h-3" />
                    Caricata il {new Date(ps.created_at).toLocaleDateString('it-IT')}
                  </div>
                </div>
              </div>
              {ps.notes && <div className="text-xs bg-slate-50 rounded p-2 mb-2 text-slate-700">{ps.notes}</div>}
              <Button size="sm" className="w-full bg-blue-700 hover:bg-blue-800 gap-1.5" onClick={() => downloadBlob(ps)}>
                <Download className="w-4 h-4" /> Scarica busta paga
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
